import * as React from "react";

import type { MediaDockKind } from "@/components/OmniCine/Docks/shared/mediaDock.types";

const waveformBarCache = new Map<string, number[]>();

type MediaPanelWaveformMode = "closed" | "ingest" | "focus" | "overlay";

type UseAudioWaveformModelArgs = {
  previewKind: MediaDockKind;
  previewUrl: string | null;
  mediaIsPlaying: boolean;
  panelMode: MediaPanelWaveformMode;
  totalBars?: number;
};

type UseAudioWaveformModelResult = {
  waveformBars: number[];
  waveMotionPhase: number;
};

function createFallbackWaveformBars(totalBars: number): number[] {
  const base = [
    22, 38, 54, 28, 64, 42, 58, 26, 70, 44, 62, 30, 52, 36, 66, 24,
    48, 72, 34, 60, 40, 56, 32, 68, 46, 50, 74, 29, 63, 41, 57, 35,
    69, 27, 53, 39, 61, 33, 67, 31,
  ];
  return Array.from({ length: totalBars }, (_, i) => base[i % base.length]);
}

function buildWaveformBarsFromBuffer(buffer: AudioBuffer, totalBars: number): number[] {
  const channelCount = Math.max(1, buffer.numberOfChannels);
  const bucketSize = Math.max(1, Math.floor(buffer.length / totalBars));
  const peaks = new Array<number>(totalBars).fill(0);
  const channelData = Array.from({ length: channelCount }, (_, ch) => buffer.getChannelData(ch));

  for (let barIndex = 0; barIndex < totalBars; barIndex += 1) {
    const start = barIndex * bucketSize;
    const end = Math.min(buffer.length, start + bucketSize);
    let peak = 0;

    for (let i = start; i < end; i += 1) {
      for (let ch = 0; ch < channelCount; ch += 1) {
        const sample = Math.abs(channelData[ch]?.[i] ?? 0);
        if (sample > peak) peak = sample;
      }
    }

    peaks[barIndex] = peak;
  }

  const maxPeak = peaks.reduce((m, p) => (p > m ? p : m), 0);
  if (maxPeak <= 0) return createFallbackWaveformBars(totalBars);

  return peaks.map((peak) => {
    const normalized = peak / maxPeak;
    const eased = Math.pow(normalized, 0.58);
    return Math.max(20, Math.round(20 + eased * 80));
  });
}

function areWaveformBarsEqual(a: number[], b: number[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }

  return true;
}

function getWaveformCacheKey(previewUrl: string, totalBars: number): string {
  return `${previewUrl}::${totalBars}`;
}

export default function useAudioWaveformModel({
  previewKind,
  previewUrl,
  mediaIsPlaying,
  panelMode,
  totalBars = 96,
}: UseAudioWaveformModelArgs): UseAudioWaveformModelResult {
  const fallbackWaveformBars = React.useMemo(() => createFallbackWaveformBars(totalBars), [totalBars]);
  const [waveformBars, setWaveformBars] = React.useState<number[]>(fallbackWaveformBars);
  const [waveMotionPhase, setWaveMotionPhase] = React.useState<number>(0);
  const waveMotionRafRef = React.useRef<number | null>(null);
  const decodeSeqRef = React.useRef(0);

  React.useEffect(() => {
    if (
      previewKind !== "audio" ||
      !previewUrl ||
      (panelMode !== "focus" && panelMode !== "overlay")
    ) {
      setWaveformBars((prev) => (areWaveformBarsEqual(prev, fallbackWaveformBars) ? prev : fallbackWaveformBars));
      return;
    }

    const cacheKey = getWaveformCacheKey(previewUrl, totalBars);
    const resolvedPreviewUrl = previewUrl;
    const cachedBars = waveformBarCache.get(cacheKey);
    if (cachedBars) {
      setWaveformBars((prev) => (areWaveformBarsEqual(prev, cachedBars) ? prev : cachedBars));
      return;
    }

    if (typeof window === "undefined") return;
    const AudioContextCtor = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) {
      setWaveformBars((prev) => (areWaveformBarsEqual(prev, fallbackWaveformBars) ? prev : fallbackWaveformBars));
      return;
    }

    const controller = new AbortController();
    const decodeId = decodeSeqRef.current + 1;
    decodeSeqRef.current = decodeId;
    let alive = true;
    const audioContext: AudioContext = new AudioContextCtor();

    async function decodeWaveform() {
      try {
        const response = await fetch(resolvedPreviewUrl, { signal: controller.signal });
        if (!response.ok) throw new Error(`waveform fetch failed: ${response.status}`);

        const audioData = await response.arrayBuffer();
        if (!alive || decodeSeqRef.current !== decodeId) return;

        const decoded = await audioContext.decodeAudioData(audioData.slice(0));
        if (!alive || decodeSeqRef.current !== decodeId) return;

        const nextBars = buildWaveformBarsFromBuffer(decoded, totalBars);
        waveformBarCache.set(cacheKey, nextBars);
        setWaveformBars((prev) => (areWaveformBarsEqual(prev, nextBars) ? prev : nextBars));
      } catch (error) {
        if (!alive || decodeSeqRef.current !== decodeId) return;
        if (error instanceof DOMException && error.name === "AbortError") return;
        setWaveformBars((prev) => (areWaveformBarsEqual(prev, fallbackWaveformBars) ? prev : fallbackWaveformBars));
      } finally {
        try {
          await audioContext.close();
        } catch {
          // ignore
        }
      }
    }

    decodeWaveform();

    return () => {
      alive = false;
      controller.abort();
      try {
        void audioContext.close();
      } catch {
        // ignore
      }
    };
  }, [fallbackWaveformBars, panelMode, previewKind, previewUrl, totalBars]);

  React.useEffect(() => {
    if (waveMotionRafRef.current != null) {
      cancelAnimationFrame(waveMotionRafRef.current);
      waveMotionRafRef.current = null;
    }

    if (!mediaIsPlaying || previewKind !== "audio" || (panelMode !== "focus" && panelMode !== "overlay")) {
      setWaveMotionPhase(0);
      return;
    }

    let alive = true;
    const tick = () => {
      if (!alive) return;
      setWaveMotionPhase((prev) => (prev + 0.2) % (Math.PI * 2));
      waveMotionRafRef.current = requestAnimationFrame(tick);
    };

    waveMotionRafRef.current = requestAnimationFrame(tick);

    return () => {
      alive = false;
      if (waveMotionRafRef.current != null) {
        cancelAnimationFrame(waveMotionRafRef.current);
        waveMotionRafRef.current = null;
      }
    };
  }, [mediaIsPlaying, panelMode, previewKind]);

  return {
    waveformBars,
    waveMotionPhase,
  };
}